import { FormEvent, useMemo, useState } from 'react';
import apiClient from '../lib/api-client';
import { API_ENDPOINTS } from '../config/api';
import { handleApiError } from '../lib/api-client';

type Step = 'info' | 'request' | 'confirm' | 'done';

export function DeleteAccountPage() {
  const initialToken = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || '';
  }, []);

  const [step, setStep] = useState<Step>(initialToken ? 'confirm' : 'info');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [token, setToken] = useState(initialToken);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const supportMailto = useMemo(() => {
    const subject = encodeURIComponent('DropSugar account deletion request');
    const body = encodeURIComponent(
      `Please delete my DropSugar account and associated personal data.\n\nAccount email: ${email || '(add your email)'}\n\nAdditional notes:\n`,
    );
    return `mailto:support@dropsugar.io?subject=${subject}&body=${body}`;
  }, [email]);

  async function requestDeletion(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await apiClient.post(API_ENDPOINTS.AUTH.ACCOUNT_DELETION_REQUEST, {
        email: email.trim(),
        reason: reason.trim() || undefined,
      });
      const data = res.data?.data ?? res.data ?? {};
      setMessage(
        data.message ||
          'If an account exists for this email, a confirmation step is required next.',
      );
      if (data.confirmationToken) {
        setToken(data.confirmationToken);
      }
      setStep('confirm');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }

  async function confirmDeletion(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.post(API_ENDPOINTS.AUTH.ACCOUNT_DELETION_CONFIRM, {
        token: token.trim(),
      });
      const data = res.data?.data ?? res.data ?? {};
      setMessage(data.message || 'Your account has been deleted.');
      setStep('done');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4">
          <img
            src="/dropsugar-logo.jpg"
            alt="DropSugar"
            className="h-10 w-10 rounded-lg object-contain"
          />
          <div>
            <p className="text-[15px] font-semibold text-[#2c5177]">DropSugar</p>
            <p className="text-[12px] text-gray-500">Account & data deletion</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-3xl font-bold text-gray-900">Delete your DropSugar account</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
          Use this page to request deletion of your DropSugar account and the personal
          data associated with it. You do not need to have the mobile app installed.
        </p>

        <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">What gets deleted</h2>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[14px] text-gray-700">
            <li>Your account login and profile information</li>
            <li>Health entries you logged (glucose, meals, steps, weight, medications, etc.)</li>
            <li>App sessions and devices so you are signed out everywhere</li>
          </ul>

          <h2 className="mt-6 text-lg font-semibold text-gray-900">What may be retained</h2>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[14px] text-gray-700">
            <li>
              Limited billing / subscription records may be kept where required for
              tax, fraud prevention, or legal compliance
            </li>
            <li>Anonymized analytics that cannot identify you</li>
          </ul>

          <p className="mt-6 text-[14px] text-gray-700">
            Deletion is permanent. If you have an active paid subscription, cancel it in
            Google Play / App Store subscriptions (or contact support) before or after
            deleting your account to avoid further charges.
          </p>
        </section>

        {step === 'info' && (
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setStep('request')}
              className="rounded-xl bg-[#72a83f] px-5 py-3 text-[14px] font-semibold text-white hover:bg-[#5f8f34]"
            >
              Request account deletion
            </button>
            <a
              href={supportMailto}
              className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-center text-[14px] font-medium text-gray-800 hover:bg-slate-50"
            >
              Email support@dropsugar.io
            </a>
          </div>
        )}

        {step === 'request' && (
          <form
            onSubmit={requestDeletion}
            className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-gray-900">Step 1 — Verify your email</h2>
            <div>
              <label className="mb-1 block text-[13px] font-medium text-gray-700">
                Account email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px]"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="mb-1 block text-[13px] font-medium text-gray-700">
                Reason (optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px]"
              />
            </div>
            {error && <p className="text-[13px] text-red-600">{error}</p>}
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={loading}
                className="rounded-xl bg-[#72a83f] px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-[#5f8f34] disabled:opacity-50"
              >
                {loading ? 'Submitting…' : 'Continue'}
              </button>
              <button
                type="button"
                onClick={() => setStep('info')}
                className="rounded-xl border border-slate-300 px-5 py-2.5 text-[14px] text-gray-700"
              >
                Back
              </button>
            </div>
          </form>
        )}

        {step === 'confirm' && (
          <form
            onSubmit={confirmDeletion}
            className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-gray-900">
              Step 2 — Confirm deletion
            </h2>
            {message && <p className="text-[13px] text-gray-600">{message}</p>}
            <p className="text-[13px] text-gray-600">
              Enter the confirmation token from your email. If you did not receive one,
              contact{' '}
              <a className="text-[#2c5177] underline" href={supportMailto}>
                support@dropsugar.io
              </a>
              .
            </p>
            <div>
              <label className="mb-1 block text-[13px] font-medium text-gray-700">
                Confirmation token
              </label>
              <input
                type="text"
                required
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-[13px]"
              />
            </div>
            {error && <p className="text-[13px] text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={loading || !token.trim()}
              className="rounded-xl bg-red-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Deleting…' : 'Permanently delete my account'}
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="mt-6 rounded-2xl border border-green-200 bg-green-50 p-6">
            <h2 className="text-lg font-semibold text-green-900">Account deleted</h2>
            <p className="mt-2 text-[14px] text-green-800">
              {message ||
                'Your DropSugar account and associated personal data have been deleted.'}
            </p>
            <a href="/" className="mt-4 inline-block text-[14px] font-medium text-[#2c5177] underline">
              Return to DropSugar
            </a>
          </div>
        )}

        <p className="mt-10 text-[12px] text-gray-500">
          DropSugar · Questions?{' '}
          <a href="mailto:support@dropsugar.io" className="underline">
            support@dropsugar.io
          </a>
        </p>
      </main>
    </div>
  );
}
